import { APIRoutes } from './constants';
import { toObjectified } from 'api/baseApi';
import { IAutoISearchVehicleByAttributeRequest, IAutoISearchVehicleResponse, IAutoInfoInitializeResponse } from 'api/swaggerTypes';
import { Payload, useFetch, useFetchMutation, usePost } from 'api/reactQuery/queryService';
import { objectify, Inline } from 'api/utils';
import { IBatchInfo } from 'components/common/DataGridDevEx/DataGrid.properties';

const detailsParser = (response) => {
  if (response) {
    return {
      ...response,
    };
  }
};

const initializeResponseParser = (response: IAutoInfoInitializeResponse): IAutoInfoInitializeResponse => {
  return {
    ...response,
  };
};

const catalogueTypeChangeParser = (response: any): any => {
  return {
    ...response,
  };
};

const regoStatesParser = (response: any): any => {
  return {
    ...response,
  };
};

const searchVehicleParser = (response: any): any => {
  return {
    ...response,
  };
};

export const useInitialize = (errorHandler?: any) => {

  return usePost<Payload<{}, any>, any>
    (APIRoutes.autoInfo.initialize, null, { responseParser: initializeResponseParser, errorMessageHandlingFunc: errorHandler });
};

export const useAutoCatalogueTypeChange = (errorHandler?: any) => {

  return usePost<Payload<{ CatalogueType: number }>, any>
    (APIRoutes.autoInfo.catalogueTypeChange, null, { responseParser: catalogueTypeChangeParser, errorMessageHandlingFunc: errorHandler });
};

export const useRegoStates = (errorHandler?: any) => {

  return usePost<Payload<{ CatalogueTypeId: number }>, any>
    (APIRoutes.autoInfo.search.regoStates, null, { responseParser: regoStatesParser, errorMessageHandlingFunc: errorHandler });
};

export const useVehicleSearch = (errorHandler?: any) => {

  return usePost<Payload<{ CatalogueType: number; Licenceplate: string; SearchText: string }>, any>
    (APIRoutes.autoInfo.search.vehicleSearch, null, { responseParser: searchVehicleParser, errorMessageHandlingFunc: errorHandler });
};

export const useManualVehicleSearch = (errorHandler?: any) =>
  usePost<Payload<IAutoISearchVehicleByAttributeRequest & { CatalogueTypeId: number }>, IAutoISearchVehicleResponse>(
    APIRoutes.autoInfo.search.manualSearch,
    null,
    { inline: false, errorMessageHandlingFunc: errorHandler }
  );

export const useRetrieveAutoICatalogueProductDetails = ({
  PartId,
  ProductId,
  ErrorHandler,
}: { PartId?: number; ProductId?: number; ErrorHandler?: any }) => {
  const urlQueryParams: Record<string, number> = {};

  if (ProductId) {
    urlQueryParams.ProductId = ProductId;
  } else {
    urlQueryParams.PartId = PartId;
  }

  return useFetch<any>({
    url: APIRoutes.autoInfo.productDetails.retrieve,
    urlQueryParams,
    queryConfig: { enabled: Boolean(PartId) || Boolean(ProductId) },
    options: { inline: true, responseParser: detailsParser, errorMessageHandlingFunc: ErrorHandler },
  });
};

export const useRetrieveAutoICatalogueProductDetailsSummary = ({ ProductId, WorksaleId, ErrorHandler }: { WorksaleId: number; ProductId: number; ErrorHandler?: any }) =>
  useFetch<any>(
    {
      url: APIRoutes.autoInfo.summary.retrieve,
      urlQueryParams: { WorksaleId, IncludeImages: true },
      urlParams: { ProductId },
      queryConfig: { enabled: Boolean(WorksaleId) && Boolean(ProductId) },
      options: {
        inline: true, responseParser: (response) => {
          const { CatalogueProductDetail } = response;

          return {
            ...response,
            ...toObjectified(CatalogueProductDetail),

          };
        },
        errorMessageHandlingFunc: ErrorHandler
      }
    }
  );

const formatCamelCase = (text) => {
  // Insert a space before a capital letter, only if it's
  // preceded by a lowercase letter or followed by a lowercase letter
  return text.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
};

const vehicleDetailsResponseParser = (response: any) => {
  if (!response?.VehicleDetail) return [];

  const { VehicleDetail } = response;

  return Object.entries(VehicleDetail).map(([key, value]) => ({
    Attributes: formatCamelCase(key),
    Details: (value as any)?.Value ?? '',
  }));
};

export type IRetrieveVehicleDetailsPayload = Payload<{ VehicleId: number }, IBatchInfo>;

export const useRetrieveVehicleDetails = (VehicleId: number, ErrorHandler?: any) =>
  useFetch<any>(
    {
      url: APIRoutes.autoInfo.vehicleDetails.retrieve,
      urlParams: { VehicleId },
      queryConfig: { enabled: VehicleId > -1 },
      options: { inline: true, responseParser: vehicleDetailsResponseParser, errorMessageHandlingFunc: ErrorHandler }
    }
  );

const catalogueEntriesResponseParser = (response): any => {
  const { Catalogues } = response;

  return {
    ...response,
    Catalogues: Catalogues.map((o) => objectify(o as Inline<any>))
  };
};

export const useRetrieveCatalogueEntries = (CatalogueType: string | number, VehicleId: number, ErrorHandler?: any) =>
  useFetch<any>(
    {
      url: APIRoutes.autoInfo.catalogueEntries.retrieve,
      urlParams: { CatalogueType, VehicleId },
      queryConfig: { enabled: CatalogueType && VehicleId > 0 },
      options: { inline: true, responseParser: catalogueEntriesResponseParser, errorMessageHandlingFunc: ErrorHandler }
    }
  );

const cataloguePartsListResponseParser = (response): any => {
  const { Parts } = response;

  return {
    ...response,
    Parts: Parts.map((p) => {

      return ({
        guid: p.ItemId,
        id: p.ItemId,
        parentId: p.ParentId ?? null,
        hasChildren: Boolean(p.Children),
        name: p.ItemDescription || p.PartDescription || '',
        description: p.SubCatalogueDescription || p.PartDescription || '',
        // retail: p.Nett1 ?? 0,
        local: p.ItemQtyUOM ?? '',
        brand: p.Brand || '',
        partNumber: p.PartNumber || '',
        image: p.ThumbImageUrl || '',
        children: p.Children || '',
        ...p
      });
    }
    )
  };
};

export type IuseRetrieveCataloguePartsListPayload = Payload<{ CatalogueType: string | number; VehicleId: number; CatalogueId: number }, { SubGroupId: number }>;

export const useRetrieveCataloguePartsList = (ErrorHandler?: any) =>
  useFetchMutation<IuseRetrieveCataloguePartsListPayload, any>
    (APIRoutes.autoInfo.partsList.retrieve, null, { inline: false, responseParser: cataloguePartsListResponseParser, errorMessageHandlingFunc: ErrorHandler });

export type IuseRetrieveCataloguePartsChildrenListPayload = Payload<{ CatalogueType: string | number; VehicleId: number; CatalogueId: number; ItemId: number }, { SubGroupId: number }>;

export const useRetrieveCataloguePartsChildrenList = (ErrorHandler?: any) =>
  useFetchMutation<IuseRetrieveCataloguePartsChildrenListPayload, any>
    (APIRoutes.autoInfo.partsList.children.retrieve, null, { inline: false, responseParser: cataloguePartsListResponseParser, errorMessageHandlingFunc: ErrorHandler });

const pricingAndAvailabilityResponseParser = (response): any => {
  const { ProductDetails } = response;

  return {
    ...response,
    ProductDetails: ProductDetails.map((o) => objectify(o as Inline<any>))
  };
};

export type IuseRetrievePricingAndAvailabilityPayload = Payload<{ ProductList: any }, { CustomerId: number; Warehouse: string; WorksaleId: number }>;
export const useRetrievePricingAndAvailability = (ErrorHandler?: any) =>
  usePost<IuseRetrievePricingAndAvailabilityPayload, any>
    (APIRoutes.autoInfo.product.pricingAndAvailability.retrieve, null, { inline: true, responseParser: pricingAndAvailabilityResponseParser, errorMessageHandlingFunc: ErrorHandler });
